import { Check, Copy, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { shortAddr } from "../lib/format.ts";

/** Mono short address with copy button, optional link to /addresses/:id and explorer icon. */
export function AddressLink({
	id,
	address,
	display,
	explorer,
	head = 6,
	tail = 4,
	full = false,
	className = "",
}: {
	id?: string | null;
	address: string;
	display?: string | null;
	explorer?: string | null;
	head?: number;
	tail?: number;
	full?: boolean;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);
	const text = full ? (display ?? address) : shortAddr(display ?? address, head, tail);

	async function copy() {
		try {
			await navigator.clipboard.writeText(display ?? address);
			setCopied(true);
			setTimeout(() => setCopied(false), 1200);
		} catch {
			// clipboard unavailable
		}
	}

	return (
		<span className={`inline-flex items-center gap-1 font-mono text-xs ${className}`}>
			{id ? (
				<Link
					to={`/addresses/${id}`}
					className="text-primary hover:underline break-all"
					title={display ?? address}
				>
					{text}
				</Link>
			) : (
				<span className="text-content break-all" title={display ?? address}>
					{text}
				</span>
			)}
			<button
				type="button"
				onClick={copy}
				className="text-muted hover:text-content"
				title="Copy address"
				aria-label="Copy address"
			>
				{copied ? <Check size={12} /> : <Copy size={12} />}
			</button>
			{explorer && (
				<a
					href={explorer}
					target="_blank"
					rel="noreferrer"
					className="text-muted hover:text-content"
					title="Open in explorer"
					aria-label="Open in explorer"
				>
					<ExternalLink size={12} />
				</a>
			)}
		</span>
	);
}
